import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  mixins: [alertMixin],
  methods: {
    async consumeSAPParams() {
      if (!this.currentAccountId) return;

      const { action, phone } = this.$route.query;

      if (!action || !phone) return;
      if (!['chat', 'call'].includes(action)) return;

      const sanitizedPhone = this.sanitizePhone(phone);

      if (!this.isValidE164(sanitizedPhone)) {
        this.showAlert(this.$t('SAP_INTEGRATION.INVALID_PHONE_NUMBER'));
        // If the input has no digits at all (eg. email/garbage), preserve the raw value
        // so the user can correct it manually.
        const hasAnyDigits = /\d/.test(String(phone));
        this.redirectToCreateContact(hasAnyDigits ? sanitizedPhone : phone);
        return;
      }

      try {
        const contactId = await this.findOrCreateContact(sanitizedPhone);
        if (contactId) {
          await this.performAction(action, contactId, sanitizedPhone);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('SAP Integration Error:', error);
      }
    },

    sanitizePhone(phone) {
      if (!phone) return '';
      // Handle common international dialing prefix `00` (eg. 0055... -> +55...).
      let digits = String(phone).replace(/\D/g, '');
      if (digits.startsWith('00')) {
        digits = digits.slice(2);
      }
      return digits ? `+${digits}` : '';
    },

    isValidE164(phone) {
      return /^\+[1-9]\d{1,14}$/.test(phone);
    },

    redirectToCreateContact(phone) {
      const query = { ...this.$route.query };
      delete query.action;
      delete query.phone;
      query.open_create_modal = 'true';
      query.prefill_phone = phone;
      query.prefill_name = 'SAP Contact';

      // Avoid back-button loops re-triggering the invalid action URL.
      this.$router.replace({
        name: 'contacts_dashboard',
        params: { accountId: this.currentAccountId },
        query,
      });
    },

    async findOrCreateContact(phone) {
      // Backend filter service prepends '+' for phone_number filters.
      // So we must send digits-only here to avoid '++' mismatches.
      const phoneDigits = String(phone).replace(/^\+/, '');
      const query = {
        attribute_key: 'phone_number',
        filter_operator: 'equal_to',
        values: [phoneDigits],
        attribute_model: 'standard',
        custom_attribute_type: '',
      };

      const queryPayload = { payload: [query] };

      const contacts = await this.$store.dispatch('contacts/filter', {
        queryPayload,
        resetState: false,
      });

      if (contacts && contacts.length > 0) {
        return contacts[0].id;
      }

      const newContact = await this.$store.dispatch('contacts/create', {
        name: 'SAP Contact',
        phone_number: phone,
      });

      return newContact.id;
    },

    async performAction(action, contactId, phone) {
      const { name, params } = this.$route;

      switch (action) {
        case 'chat':
          if (
            name === 'contact_profile_dashboard' &&
            Number(params.contactId) === contactId &&
            this.$route.query.action === 'chat'
          ) {
            return;
          }

          await this.$router.push({
            name: 'contact_profile_dashboard',
            params: { accountId: this.currentAccountId, contactId },
            query: { ...this.$route.query, action: 'chat', phone },
          });
          break;

        case 'call': {
          const isHitPhoneEnabled = this.$store.getters[
            'accounts/isFeatureEnabledonAccount'
          ](this.currentAccountId, FEATURE_FLAGS.HIT_PHONE);

          if (!isHitPhoneEnabled) {
            this.showAlert(this.$t('SAP_INTEGRATION.HITPHONE_NOT_ENABLED'));
          }

          await this.$router.push({
            name: 'contact_profile_dashboard',
            params: { accountId: this.currentAccountId, contactId },
            query: { ...this.$route.query, action: 'call', phone },
          });

          if (isHitPhoneEnabled) {
            this.$emitter.emit('hitphone:call', { phone });
          }
          break;
        }
        default:
          break;
      }
    },
  },
};
